import { Link } from 'react-router-dom'
import {MapPin, ArrowRight} from 'lucide-react'
import SectionHeading from '@/components/SectionHeading'

const regions = [
  {
    region: 'West Coast',
    cities: ['Vancouver', 'Victoria', 'Surrey', 'Burnaby', 'Richmond', 'Kelowna'],
  },
  {
    region: 'Prairies',
    cities: ['Calgary', 'Edmonton', 'Saskatoon', 'Regina', 'Winnipeg'],
  },
  {
    region: 'Central Canada',
    cities: ['Toronto', 'Ottawa', 'Mississauga', 'Hamilton', 'London', 'Kitchener-Waterloo'],
  },
  {
    region: 'Quebec',
    cities: ['Montreal', 'Quebec City', 'Laval', 'Gatineau', 'Sherbrooke'],
  },
  {
    region: 'Atlantic Canada',
    cities: ['Halifax', 'Moncton', 'Fredericton', 'St. John\'s', 'Charlottetown'],
  },
]

function ServiceAreas() {
  return (
    <div>
      {/* HERO */}
      <section className="relative bg-navy text-white py-24 md:py-32 overflow-hidden">
        <img
          src="https://static.lumi.new/7a/7aecc72fc43949081279abfe8b66fa93.png"
          alt="Canada service coverage map"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/50" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-orange-accent" />
              <span className="text-xs uppercase tracking-[0.3em] text-orange-accent font-semibold">Service Areas</span>
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05]">
              From coast <span className="text-orange-accent">to coast to coast.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-white/80 leading-relaxed">
              Delta operates in every major Canadian market, with local teams backed by national infrastructure. Same standards. Same accountability. Everywhere.
            </p>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
              <div>
                <div className="font-display text-4xl font-bold text-orange-accent">30+</div>
                <div className="text-xs uppercase tracking-wider text-white/70 mt-2">Cities Served</div>
              </div>
              <div>
                <div className="font-display text-4xl font-bold text-orange-accent">10</div>
                <div className="text-xs uppercase tracking-wider text-white/70 mt-2">Provinces Covered</div>
              </div>
              <div>
                <div className="font-display text-4xl font-bold text-orange-accent">1</div>
                <div className="text-xs uppercase tracking-wider text-white/70 mt-2">Unified Standard</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REGIONS */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading
            centered
            eyebrow="Regional Coverage"
            title="Local teams. National standards."
            description="Each region operates with its own dedicated operations team, supported by Delta's centralized quality, training, and procurement infrastructure."
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regions.map((r, i) => (
              <div
                key={r.region}
                className="group bg-white border border-soft rounded-2xl p-8 hover-lift"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-navy group-hover:bg-orange-accent transition-colors flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-navy">{r.region}</h3>
                </div>
                <ul className="space-y-2.5">
                  {r.cities.map((c) => (
                    <li key={c} className="flex items-center gap-3 text-navy/85 text-sm">
                      <span className="w-1 h-1 rounded-full bg-orange-accent" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCALABILITY */}
      <section className="py-24 md:py-32 bg-grey-soft">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <SectionHeading
            centered
            eyebrow="Scalability"
            title="Expanding with you — wherever you grow."
            description="Multi-property portfolio? Multi-province footprint? We consolidate your facility services into a single contract, single point-of-contact, and single reporting dashboard — no matter where your buildings are."
          />
          <Link
            to="/quote"
            className="mt-10 inline-flex items-center gap-2 bg-orange-accent hover:bg-orange-dark text-white px-8 py-4 rounded-md font-semibold transition-all group shadow-lg shadow-orange-accent/30"
          >
            Discuss Your Portfolio <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  )
}

export default ServiceAreas
